/**
 * NotificationBell.tsx
 *
 * Topbar notification bell button with:
 *   - Red dot badge showing unread count (capped at 99+)
 *   - Click → dropdown panel showing the 50 most recent notifications
 *   - Each row: icon (by type), title, relative timestamp, unread highlight
 *   - Click on a row: marks it read + navigates to its deep-link (if any)
 *   - "Mark all read" button in the panel header
 *   - Empty state when there are no notifications
 *   - Closes on outside click (same pattern as ThemeSwitcher)
 *
 * Notification type → icon/colour mapping:
 *   leave_*               → CalendarDays  (blue)
 *   kpi_blocker           → Flag          (red, before kpi_*)
 *   kpi_*                 → TrendingUp    (purple)
 *   task_*                → CheckSquare   (teal)
 *   ticket_*              → LifeBuoy      (orange)
 *   expense_*             → DollarSign    (green)
 *   budget_threshold      → AlertTriangle (red)
 *   compliance_alert      → ShieldAlert   (amber)
 *   mention               → AtSign        (pink)
 *   announcement          → Megaphone     (yellow)
 *   platform_announcement → Megaphone     (orange)   v6.80
 *   channel_*             → Hash          (indigo)   v6.90-95
 *   system / other        → Bell          (slate)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, BellOff,
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import type { AppNotification } from '../../types';
import { getNotificationIconConfig } from './notificationIcons';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Relative time label: "just now", "5m ago", "2h ago", "3d ago" */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Icon mapping lives in `./notificationIcons.tsx` so the bell + the inbox
// page (`pages/NotificationsPage.tsx`) share one source of truth. Extracted
// in v7.1 from duplicated copies that drifted between v5 and v7.0.

// ── Notification row ──────────────────────────────────────────────────────────

interface NotifRowProps {
  notif: AppNotification;
  onRead: (id: string) => void;
}

const NotifRow = React.memo(function NotifRow({ notif, onRead }: NotifRowProps) {
  const navigate = useNavigate();
  const { icon, bg } = getNotificationIconConfig(notif.type);
  const isUnread = !notif.read_at;

  const handleClick = () => {
    onRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors duration-150 hover:bg-white/10/5 ${
        isUnread ? 'bg-white/[0.04]' : ''
      }`}
    >
      {/* Type icon */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${bg}`}>
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug truncate ${isUnread ? 'font-semibold text-white' : 'text-white/80'}`}>
          {notif.title}
        </p>
        {notif.body && (
          <p className="text-xs text-white/50 mt-0.5 line-clamp-2">{notif.body}</p>
        )}
        <p className="text-xs text-white/35 mt-1">{relativeTime(notif.created_at)}</p>
      </div>

      {/* Unread dot */}
      {isUnread && (
        <span className="flex-shrink-0 w-2 h-2 rounded-full bg-teal-400 mt-2" />
      )}
    </button>
  );
});

// ── Bell button + dropdown ────────────────────────────────────────────────────

export default function NotificationBell({
  buttonClassName = '',
  icon,
}: {
  buttonClassName?: string;
  /** Optional bell glyph override (the dock passes its ghost-outline set icon);
      default stays the lucide bell for every other surface. */
  icon?: React.ReactNode;
}) {
  const { notifications, unreadCount, loading, error, refresh, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  /** Close on outside click */
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = useCallback((id: string) => {
    markRead(id);
  }, [markRead]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div ref={containerRef} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={`Notifications${unreadCount ? ` (${unreadCount} unread)` : ''}`}
        className={`relative p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors ${buttonClassName}`}
      >
        {icon ?? <Bell size={20} />}
        {unreadCount > 0 && (
          /* Compact bordered corner pill — see .pq-bell-badge. The old Tailwind
             span (min-w-16 h-4 rounded-full, no border) read as a big red blob on
             the small tile; this is a bordered pill clearly smaller than the bell. */
          <span className="pq-bell-badge">{badgeLabel}</span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl z-50 overflow-hidden"
          style={{
            background: 'rgba(15, 23, 42, 0.97)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => { markAllRead(); }}
                className="flex items-center gap-1.5 text-xs text-teal-400 hover:text-teal-300 transition-colors"
              >
                <Check size={12} />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-10 text-white/40 text-sm">
                Loading…
              </div>
            )}

            {/* Never fail silently: a failed inbox fetch shows an error (+ retry),
                not a misleading "No notifications yet". */}
            {!loading && error && (
              <div role="alert" className="flex flex-col items-center justify-center py-8 gap-2 px-4 text-center">
                <p className="text-sm text-red-300">Couldn’t load notifications</p>
                <p className="text-xs text-white/40 break-words">{error}</p>
                <button onClick={() => { void refresh(); }} className="text-xs text-teal-400 hover:text-teal-300 mt-1">
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && notifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 gap-3 text-white/40">
                <BellOff size={28} className="opacity-40" />
                <p className="text-sm">No notifications yet</p>
              </div>
            )}

            {!loading && notifications.map(n => (
              <NotifRow
                key={n.id}
                notif={n}
                onRead={handleMarkRead}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between">
            <p className="text-xs text-white/30">
              {notifications.length > 0
                ? `Showing last ${notifications.length}`
                : 'Up to date'}
            </p>
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="text-xs text-teal-400 hover:text-teal-300 font-medium"
            >
              View all →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
